import { useState } from 'react'

const WHATSAPP_FUNCTIONS = [
  {
    name: 'Send-WhatsApp',
    description: 'Send WhatsApp text or image messages via Meta Graph API (canonical sender)',
    path: '/supabase/functions/Send-WhatsApp/index.ts',
    method: 'POST',
    endpoint: '/functions/v1/Send-WhatsApp',
    architecturalNotes: [
      'Canonical name is Send-WhatsApp (convention <Domain>-<Action>-WhatsApp, channel as suffix — ADR-0005 E2b).',
      'DEPRECATED alias: the old name WhatsApp-Send (/functions/v1/WhatsApp-Send) still works — it is a thin dual-serving re-import that serves identical bytes through the deprecation window. Prefer Send-WhatsApp for new integrations.',
      'Optional imageUrl (+ optional caption) sends a Meta type:image message; omit imageUrl for the unchanged text path.'
    ],
    features: [
      {
        name: 'Optional image messages',
        summary: 'Send an image by URL with an optional caption',
        detail: 'When imageUrl is supplied the function sends a Meta type:image message ({ image: { link, caption } }); caption defaults to the message field if not given. When imageUrl is absent the behaviour is the unchanged text path (message required). Purely additive — existing text callers are unaffected.'
      },
      {
        name: 'API key authentication',
        summary: 'Secure API key validation against WhatsAppApiCredentials',
        detail: 'Authenticates requests using X-Api-Key header or Authorization Bearer token. Keys are validated against the WhatsAppApiCredentials table to ensure only authorized accounts can send messages. Each key is linked to a specific WhatsAppAccount with configurable permissions.'
      },
      {
        name: 'Meta WhatsApp Cloud API integration',
        summary: 'Direct integration with Meta Graph API for message delivery',
        detail: 'Utilizes Meta\'s WhatsApp Cloud API (Graph API v17.0+) for reliable message delivery. Handles OAuth bearer token authentication, constructs proper message payloads, and manages API responses including rate limits and error handling.'
      },
      {
        name: 'E.164 phone number format',
        summary: 'International phone number standard for global reach',
        detail: 'Enforces E.164 format (+[country code][number]) for all phone numbers, ensuring compatibility with WhatsApp\'s international infrastructure. Format validation happens before API calls to prevent delivery failures.'
      },
      {
        name: 'Automatic WhatsAppEntries logging',
        summary: 'Every message is logged to database for audit and tracking',
        detail: 'Creates a WhatsAppEntries record for each send attempt, capturing ToNumber, FromNumber, MessageType, timestamps, and account references. This enables delivery tracking, webhook correlation, compliance reporting, and historical analysis.'
      },
      {
        name: 'External message ID tracking',
        summary: 'Stores Meta\'s message ID for webhook event correlation',
        detail: 'Captures the wamid (WhatsApp Message ID) returned by Meta\'s API and stores it in WhatsAppEntries.ExternalId. This ID is crucial for correlating delivery webhooks (sent, delivered, read, failed) back to the original message.'
      }
    ],
    businessLogic: [
      { step: 1, title: 'CORS Handling', description: 'Handles preflight OPTIONS requests and includes appropriate CORS headers for cross-origin requests' },
      { step: 2, title: 'Authentication', description: 'Validates API key from request headers (X-Api-Key or Authorization Bearer token) against WhatsAppApiCredentials table' },
      { step: 3, title: 'Input Validation', description: 'Ensures toNumber (E.164 format) and message fields are present and non-empty' },
      { step: 4, title: 'Account Lookup', description: 'Retrieves WhatsAppAccount associated with the API key, ensuring account is not deleted' },
      { step: 5, title: 'Provider Configuration', description: 'Fetches WhatsAppProviderConfig with AccessToken, PhoneNumberId, and GraphBase URL from JsonConfig' },
      { step: 6, title: 'Provider Validation', description: 'Verifies provider is META/META_PUSH/WHATSAPP/META WHATSAPP (case-insensitive)' },
      { step: 7, title: 'Database Logging', description: 'Creates WhatsAppEntries record to track this message send attempt with account and config references' },
      { step: 8, title: 'Meta Graph API Payload', description: 'Builds Meta WhatsApp API request with messaging_product, to number, and either type:text (message body) or type:image (image link + optional caption when imageUrl is supplied)' },
      { step: 9, title: 'Send via Meta API', description: 'POSTs to Meta Graph API (default: v24.0) with bearer token authorization, captures message ID from response' },
      { step: 10, title: 'Update External ID', description: 'Stores Meta message ID (messages[0].id) in WhatsAppEntries.ExternalId for webhook correlation' },
      { step: 11, title: 'Return Response', description: 'Returns 202 Accepted with status "queued", provider, whatsAppEntryId, and externalId for client tracking' }
    ],
    requestExample: {
      headers: {
        'X-Api-Key': 'your-api-key-uuid',
        'Content-Type': 'application/json'
      },
      body: {
        toNumber: '+27821234567',
        message: 'Hello from Nudgge!',
        imageUrl: 'https://cdn.example.com/reward.png (optional — sends an image; caption falls back to message)'
      }
    },
    responseExample: {
      status: 'queued',
      provider: 'META_WHATSAPP',
      whatsappEntryId: 123,
      externalId: 'wamid.HBgNMjc4MjEyMzQ1NjcVAgARGBI5...'
    }
  },
  {
    name: 'Webhook-WhatsApp',
    description: 'Safely receives WhatsApp webhook events, validates them, extracts delivery status updates, stores them for tracking, and responds without blocking Meta\'s delivery pipeline',
    path: '/supabase/functions/Webhook-WhatsApp/index.ts',
    method: 'GET, POST',
    endpoint: '/functions/v1/Webhook-WhatsApp',
    features: [
      {
        name: 'Non-blocking webhook processing (always returns 200 OK)',
        summary: 'Acknowledges Meta webhooks immediately to prevent retry storms',
        detail: 'Returns HTTP 200/202 status to Meta immediately after receiving the webhook, regardless of internal processing success. This prevents Meta from retrying the webhook indefinitely. Internal errors are logged but do not block the response, ensuring reliable webhook delivery even during partial system failures.'
      },
      {
        name: 'Idempotent event handling (safe for Meta retries)',
        summary: 'Handles duplicate webhook deliveries gracefully',
        detail: 'Meta may send the same webhook multiple times due to network issues or retries. The function uses UPSERT operations and timestamp-based deduplication to ensure the same event is not processed twice. This guarantees data integrity even when Meta retries webhook delivery.'
      },
      {
        name: 'Real-time delivery status tracking',
        summary: 'Captures sent, delivered, read, and failed statuses as they occur',
        detail: 'Processes delivery webhooks in real-time as Meta sends them. Each status update (sent → delivered → read, or failed) is immediately stored in WhatsAppEntryEvents with precise timestamps. This enables live dashboards, instant delivery reports, and timely failure notifications.'
      },
      {
        name: 'Graceful failure handling',
        summary: 'Continues processing remaining events even if one fails',
        detail: 'Uses per-event try-catch blocks to isolate failures. If one status update fails to process (e.g., orphaned message, database error), the function logs the error and continues processing remaining events in the batch. This ensures maximum data capture even during partial failures.'
      },
      {
        name: 'Runs as Edge Function for low latency and auto-scaling',
        summary: 'Deployed globally for fast webhook processing and automatic scaling',
        detail: 'Runs as a Supabase Edge Function (Deno Deploy) with global distribution and automatic scaling. Responds to Meta webhooks in <100ms from any region. Scales automatically from 0 to thousands of concurrent requests without manual intervention or infrastructure management.'
      }
    ],
    architecturalNotes: [
      'This function is non-blocking and always returns 200 OK to Meta',
      'Webhook retries are expected and handled safely (idempotent behavior)',
      'Runs as an Edge Function for low latency, auto-scaling, and global distribution',
      'Partial failures do not prevent Meta from considering the webhook successful'
    ],
    phases: [
      {
        name: 'Phase 1: Gatekeeping',
        purpose: 'Ensure the request is allowed and genuinely from Meta',
        why: 'Prevents spoofed requests and accidental data pollution',
        steps: [1, 2]
      },
      {
        name: 'Phase 2: Interpretation',
        purpose: 'Understand the WhatsApp payload and ignore irrelevant data',
        why: 'Meta sends various event types; we only process what matters for delivery tracking',
        steps: [3, 4]
      },
      {
        name: 'Phase 3: Classification',
        purpose: 'Determine what kind of WhatsApp event occurred and link it to an internal message',
        why: 'Each status update must be correctly categorized and matched to the original message',
        steps: [5, 6]
      },
      {
        name: 'Phase 4: Persistence',
        purpose: 'Create a clean, reliable record for tracking, analytics, and audits',
        why: 'Delivery data powers dashboards, billing, and compliance reporting',
        steps: [7, 8]
      },
      {
        name: 'Phase 5: Response & Resilience',
        purpose: 'Ensure Meta always receives a successful response, even if internal processing partially fails',
        why: 'Webhook failures cause Meta to retry indefinitely; we acknowledge receipt immediately',
        steps: [9, 10]
      }
    ],
    businessLogic: [
      { 
        step: 1, 
        phase: 'Gatekeeping',
        title: 'Allow Cross-Origin Requests', 
        description: 'Handles OPTIONS preflight and sets appropriate CORS headers for webhook calls',
        layman: 'Confirms Meta is allowed to send data to this endpoint',
        technical: 'Validates CORS preflight, returns appropriate CORS headers for Meta\'s webhook origin'
      },
      { 
        step: 2, 
        phase: 'Gatekeeping',
        title: 'Verify Source Authenticity', 
        description: 'For GET requests, verifies hub.mode=subscribe, hub.verify_token matches WHATSAPP_VERIFY_TOKEN env var, returns hub.challenge to complete Meta webhook setup',
        layman: 'Proves to Meta we control this URL during initial setup',
        technical: 'GET requests return hub.challenge if hub.verify_token matches WHATSAPP_VERIFY_TOKEN env var'
      },
      { 
        step: 3, 
        phase: 'Interpretation',
        title: 'Understand Payload', 
        description: 'Parses JSON body containing entry array from Meta webhook (Meta sends batches of events)',
        layman: 'Reads the delivery status updates Meta is sending',
        technical: 'Extracts request.body JSON, validates structure, handles malformed payloads gracefully'
      },
      { 
        step: 4, 
        phase: 'Interpretation',
        title: 'Extract Status Events', 
        description: 'Iterates through entry[].changes[].value.statuses[] to extract all status updates',
        layman: 'Finds the specific delivery updates in Meta\'s data structure',
        technical: 'Iterates entry[].changes[].value.statuses[], ignoring non-status webhooks'
      },
      { 
        step: 5, 
        phase: 'Classification',
        title: 'Categorize Event Type', 
        description: 'For each status, gets or creates WhatsAppEventTypes record (e.g., "sent", "delivered", "read", "failed")',
        layman: 'Labels the event as "delivered", "read", "failed", etc.',
        technical: 'Queries WhatsAppEntryEventTypes table, creates new type if first occurrence'
      },
      { 
        step: 6, 
        phase: 'Classification',
        title: 'Link to Original Message', 
        description: 'Looks up WhatsAppEntries by status.id (ExternalId) to find the corresponding message',
        layman: 'Connects this status update to the message we originally sent',
        technical: 'Queries WhatsAppEntries by ExternalId (Meta\'s wamid), returns null if not found'
      },
      { 
        step: 7, 
        phase: 'Persistence',
        title: 'Extract Event Metadata', 
        description: 'Parses status fields: status (delivered/read/failed), id (message ID), timestamp, errors[0].message (failure reason)',
        layman: 'Collects all the details about what happened and when',
        technical: 'Parses status, timestamp (Unix), errors.code, errors.title, conversation.id'
      },
      { 
        step: 8, 
        phase: 'Persistence',
        title: 'Store Tracking Record', 
        description: 'Inserts into WhatsAppEntryEvents with entry ID, event type, external ID, status, reason, and timestamp',
        layman: 'Saves this delivery status to our database permanently',
        technical: 'INSERT with WhatsAppEntryId FK, handles duplicate timestamps via UPSERT if needed'
      },
      { 
        step: 9, 
        phase: 'Response & Resilience',
        title: 'Continue Despite Errors', 
        description: 'Logs errors per event but continues processing remaining events in batch (non-blocking)',
        layman: 'If one status update fails, we still handle the others',
        technical: 'try-catch per status, collects errors[], continues iteration (non-blocking)'
      },
      { 
        step: 10, 
        phase: 'Response & Resilience',
        title: 'Acknowledge Receipt', 
        description: 'Returns 202 with count of events received and per-event success/failure results array',
        layman: 'Tells Meta "we got it" so they stop retrying',
        technical: 'Always returns 200-level status to prevent webhook retry storms, includes processed count'
      }
    ],
    webhookSetup: {
      steps: [
        '1. Go to Meta Developer Portal → Your App → WhatsApp → Configuration',
        '2. In "Webhook" section, add callback URL: https://your-project.supabase.co/functions/v1/Webhook-WhatsApp',
        '3. Set verify token (any string) and add it to WHATSAPP_VERIFY_TOKEN env var in Supabase',
        '4. Subscribe to webhook fields: messages, message_status',
        '5. Test the webhook - Meta will send a GET request to verify'
      ],
      verifyTokenNote: 'The verify token is a secret string you choose. Add it to Supabase Edge Function secrets: WHATSAPP_VERIFY_TOKEN'
    },
    webhookPayloadExample: {
      entry: [
        {
          id: '103649782659516',
          changes: [
            {
              value: {
                messaging_product: 'whatsapp',
                metadata: {
                  display_phone_number: '27821234567',
                  phone_number_id: '12345678'
                },
                statuses: [
                  {
                    id: 'wamid.HBgNMjc4MjEyMzQ1NjcVAgARGBI5...',
                    status: 'delivered',
                    timestamp: '1702654321',
                    recipient_id: '27821234567'
                  },
                  {
                    id: 'wamid.HBgNMjc4MjEyMzQ1NjcVAgARGBI5...',
                    status: 'read',
                    timestamp: '1702654322',
                    recipient_id: '27821234567'
                  }
                ]
              },
              field: 'messages'
            }
          ]
        }
      ]
    }
  },
  {
    name: 'Send-Templated-WhatsApp',
    description: 'Send pre-approved WhatsApp template messages with dynamic parameters (canonical sender)',
    path: '/supabase/functions/Send-Templated-WhatsApp/index.ts',
    method: 'POST',
    endpoint: '/functions/v1/Send-Templated-WhatsApp',
    architecturalNotes: [
      'Canonical name is Send-Templated-WhatsApp (convention <Domain>-<Action>-WhatsApp, channel as suffix — ADR-0005 E2b).',
      'DEPRECATED alias: the old name Send-WhatsApp-Templated (/functions/v1/Send-WhatsApp-Templated) still works — it is a thin dual-serving re-import that serves identical bytes through the deprecation window. Prefer Send-Templated-WhatsApp for new integrations.'
    ],
    features: [
      {
        name: 'API key authentication',
        summary: 'Secure API key validation for authorized template sending',
        detail: 'Authenticates requests using X-Api-Key header or Authorization Bearer token against WhatsappApiCredentials table. Each key is linked to a specific WhatsAppAccount with template permissions. Only authorized accounts can send templates, preventing unauthorized usage and ensuring compliance with Meta\'s template policies.'
      },
      {
        name: 'Meta WhatsApp template message support',
        summary: 'Send pre-approved template messages for notifications and marketing',
        detail: 'Integrates with Meta\'s WhatsApp Business API to send template messages - pre-approved message formats required for business-initiated conversations. Templates enable notifications (order updates, appointment reminders) and marketing campaigns while complying with WhatsApp\'s messaging policies and avoiding account bans.'
      },
      {
        name: 'Dynamic template parameter substitution',
        summary: 'Replace template placeholders with user-specific values',
        detail: 'Supports dynamic parameter substitution in template body components. Pass an array or object of values that replace {{1}}, {{2}}, etc. placeholders in your template. For example, "Hello {{1}}, your order {{2}} is ready" becomes "Hello John, your order #12345 is ready". Parameters are automatically formatted and validated before sending.'
      },
      {
        name: 'Template validation against WhatsAppTemplates',
        summary: 'Ensures templates exist and are approved before sending',
        detail: 'Validates templateIdentifier (UUID) against WhatsAppTemplates table to ensure the template exists, is linked to the correct account, has Name and Language fields, and is not deleted. This prevents sending attempts with invalid or unapproved templates, which would result in Meta API errors and potential account penalties.'
      },
      {
        name: 'Automatic WhatsAppEntries logging',
        summary: 'Logs every template send with MessageType and TemplateIdentifier',
        detail: 'Creates WhatsAppEntries records with MessageType="template" and TemplateIdentifier for audit trails and analytics. Enables tracking of template performance, A/B testing different templates, compliance reporting, and correlation with delivery webhooks. All template sends are logged regardless of success/failure.'
      },
      {
        name: 'Tracking properties support',
        summary: 'Attach custom metadata for campaign tracking and analytics',
        detail: 'Store arbitrary key-value pairs (e.g., campaign_id, promotion_code) in WhatsAppEntryAttributes table alongside template sends. These properties enable granular reporting by campaign, user segment, or promotion. Track ROI, conversion rates, and engagement metrics per template or campaign without modifying template content.'
      }
    ],
    businessLogic: [
      { step: 1, title: 'CORS Handling', description: 'Handles preflight OPTIONS requests and includes appropriate CORS headers' },
      { step: 2, title: 'Authentication', description: 'Validates API key from request headers against WhatsappApiCredentials table' },
      { step: 3, title: 'Input Validation', description: 'Ensures toNumber (E.164) and templateIdentifier (UUID) are present and valid' },
      { step: 4, title: 'Account Lookup', description: 'Retrieves WhatsappAccount associated with the API key' },
      { step: 5, title: 'Provider Configuration', description: 'Fetches WhatsappProviderConfig with AccessToken, PhoneNumberId, and GraphBase URL' },
      { step: 6, title: 'Provider Validation', description: 'Verifies provider is META/META_PUSH/WHATSAPP/META WHATSAPP' },
      { step: 7, title: 'Template Lookup', description: 'Retrieves template Name and Language from WhatsappTemplates by templateIdentifier' },
      { step: 8, title: 'Database Logging', description: 'Creates WhatsappEntries record with MessageType="template" and TemplateIdentifier' },
      { step: 9, title: 'Tracking Properties', description: 'Stores custom tracking key-value pairs in WhatsappEntryAttributes table' },
      { step: 10, title: 'Build Template Payload', description: 'Constructs Meta template message with name, language, and dynamic parameters' },
      { step: 11, title: 'Send via Meta API', description: 'POSTs template message to Meta Graph API with bearer token' },
      { step: 12, title: 'Update External ID', description: 'Stores Meta message ID in WhatsappEntries.ExternalId for tracking' },
      { step: 13, title: 'Return Response', description: 'Returns 202 Accepted with status, whatsappEntryId, templateIdentifier, and externalId' }
    ],
    requestExample: {
      headers: {
        'X-Api-Key': 'your-api-key-uuid',
        'Content-Type': 'application/json'
      },
      body: {
        toNumber: '+27821234567',
        templateIdentifier: 'template-uuid-here',
        templateParameters: ['John', 'Monday', '15:00'],
        trackingProperties: [
          { key: 'campaign_id', value: 'template_campaign_123' }
        ]
      }
    },
    responseExample: {
      status: 'queued',
      provider: 'META_WHATSAPP',
      whatsappEntryId: 456,
      templateIdentifier: 'template-uuid-here',
      externalId: 'wamid.HBgNMjc4MjEyMzQ1NjcVAgARGBI5...'
    }
  },
  {
    name: 'OTP-Request-WhatsApp',
    description: 'Generate and send OTP codes via WhatsApp with rate limiting and cooldowns',
    path: '/supabase/functions/OTP-Request-WhatsApp/index.ts',
    method: 'POST',
    endpoint: '/functions/v1/OTP-Request-WhatsApp',
    features: [
      {
        name: 'API key authentication via OTPApiCredentials',
        summary: 'Separate OTP API keys for security and access control',
        detail: 'Uses dedicated OTPApiCredentials table (separate from WhatsApp messaging keys) to authenticate OTP requests. Each key is linked to an OTPAccount with specific rate limits and expiry settings. This isolation ensures OTP functionality can be controlled independently from general WhatsApp messaging, with different security policies and permissions.'
      },
      {
        name: 'GUID-based OTP code generation (secure UUIDs)',
        summary: 'Cryptographically secure UUID codes instead of numeric PINs',
        detail: 'Generates full UUID v4 codes (36 characters: 8-4-4-4-12) using crypto.randomUUID() for maximum security. Unlike 4-6 digit numeric codes that are vulnerable to brute force, UUIDs provide 128 bits of entropy (2^128 possible values). Users reply with the full UUID for validation, eliminating guessing attacks.'
      },
      {
        name: 'Configurable expiry (default 10 minutes)',
        summary: 'Set custom OTP validity periods per account or request',
        detail: 'Supports per-account DefaultExpiry settings in OTPAccounts table and per-request expiryMinutes parameter. Codes expire after the specified duration (default 10 minutes) to limit attack windows. Expired codes are rejected during validation. Use shorter expiries (5 min) for high-security flows or longer (15 min) for better UX.'
      },
      {
        name: 'Rate limiting (per-account and per-user daily limits)',
        summary: 'Prevent abuse with configurable daily request limits',
        detail: 'Enforces MaxRequestsPerDayWhatsApp (account-wide) and MaxRequestsPerDayPerAUserWhatsApp (per phone number) limits from OTPAccounts. Prevents spam attacks and abuse by limiting OTP requests per 24-hour period. Returns 429 Too Many Requests when limits are exceeded. Limits reset at midnight UTC daily.'
      },
      {
        name: 'Resend cooldown protection',
        summary: 'Prevents rapid-fire OTP requests from the same user',
        detail: 'Checks ResendCooldownSeconds from OTPAccounts to prevent users from requesting new OTPs too quickly (e.g., 60 seconds between requests). Reduces SMS/WhatsApp costs, prevents user confusion from multiple active codes, and mitigates certain attack vectors. Returns remaining wait time in error message for better UX.'
      },
      {
        name: 'Automatic OTP delivery via WhatsApp template',
        summary: 'Seamlessly delivers OTP codes through WhatsApp infrastructure',
        detail: 'Calls WhatsApp-Send or Send-WhatsApp-Templated function internally to deliver the OTP code. Uses pre-approved WhatsApp templates for compliance with Meta policies. Tracks delivery through WhatsAppEntries/WhatsAppEntryEvents for monitoring. Provides better delivery rates and user experience compared to SMS, with read receipts and lower costs.'
      }
    ],
    businessLogic: [
      { step: 1, title: 'CORS Handling', description: 'Handles OPTIONS preflight and sets CORS headers' },
      { step: 2, title: 'API Key Authentication', description: 'Validates API key from headers against OTPApiCredentials table' },
      { step: 3, title: 'Input Validation', description: 'Ensures msisdn (mobile number) is provided and normalizes to E.164 format' },
      { step: 4, title: 'OTP Account Lookup', description: 'Retrieves OTPAccounts with DefaultExpiry, rate limits, and cooldown settings' },
      { step: 5, title: 'Compute Expiry', description: 'Calculates OTP expiration time based on expiryMinutes param or account default' },
      { step: 6, title: 'Daily Account Limit Check', description: 'Enforces MaxRequestsPerDayWhatsApp for the entire account' },
      { step: 7, title: 'Daily User Limit Check', description: 'Enforces MaxRequestsPerDayPerAUserWhatsApp for this specific msisdn' },
      { step: 8, title: 'Resend Cooldown Check', description: 'Prevents spam by checking ResendCooldownSeconds since last request' },
      { step: 9, title: 'Generate GUID Code', description: 'Creates crypto-secure UUID as OTP code (36 characters)' },
      { step: 10, title: 'Store OTP Request', description: 'Inserts into OTPRequests with code, msisdn, expiry, and channel=WhatsApp' },
      { step: 11, title: 'Send WhatsApp Message', description: 'Calls WhatsApp-Send or template function to deliver OTP code' },
      { step: 12, title: 'Return Response', description: 'Returns 200 with requestId, expiresUtc, and masked code for client display' }
    ],
    requestExample: {
      headers: {
        'X-Api-Key': 'your-otp-api-key-uuid',
        'Content-Type': 'application/json'
      },
      body: {
        msisdn: '+27821234567',
        expiryMinutes: 5
      }
    },
    responseExample: {
      requestId: 'otp-request-uuid',
      expiresUtc: '2025-12-15T20:30:00.000Z',
      maskedCode: '********-****-****-****-************'
    }
  },
  {
    name: 'OTP-Validate-WhatsApp',
    description: 'Validate OTP codes received via WhatsApp webhook and respond to users',
    path: '/supabase/functions/OTP-Validate-WhatsApp/index.ts',
    method: 'GET, POST',
    endpoint: '/functions/v1/OTP-Validate-WhatsApp',
    features: [
      {
        name: 'Webhook verification (GET) for Meta setup',
        summary: 'Completes Meta webhook configuration handshake',
        detail: 'Responds to Meta\'s GET verification request during webhook setup. Validates hub.verify_token against WHATSAPP_VERIFY_TOKEN env var and returns hub.challenge to prove endpoint ownership. This one-time verification is required before Meta will send POST webhooks with incoming messages. Ensures secure webhook configuration.'
      },
      {
        name: 'Automatic GUID extraction from incoming messages',
        summary: 'Uses regex to find UUID patterns in message text',
        detail: 'Parses incoming WhatsApp message body using regex pattern to extract UUID v4 codes (format: 8-4-4-4-12 hex characters). Handles messages with additional text before/after the code. If no valid UUID is found, sends a friendly error message to the user asking them to try again. Robust extraction prevents validation failures from user typos or extra characters.'
      },
      {
        name: 'OTP validation against OTPRequests table',
        summary: 'Verifies code and phone number match an active OTP request',
        detail: 'Queries OTPRequests table by Code (UUID) and normalized Msisdn (E.164 format) where ValidatedUtc IS NULL. Ensures the code exists, belongs to the correct user, and hasn\'t been used already. Multi-factor validation (code + phone number) prevents code theft - even if someone intercepts the code, they can\'t use it from a different number.'
      },
      {
        name: 'Expiry and usage checks',
        summary: 'Rejects expired or already-used OTP codes',
        detail: 'Validates ExpiresUtc timestamp to ensure code hasn\'t expired. Checks ValidatedUtc IS NULL to prevent code reuse (one-time use only). Returns specific error messages for expired vs. not-found codes to help users understand why validation failed. Prevents replay attacks and ensures time-bound security.'
      },
      {
        name: 'Automatic WhatsApp reply messages (success/failure)',
        summary: 'Sends immediate feedback to users via WhatsApp',
        detail: 'Automatically replies to the user\'s WhatsApp message with success confirmation or specific error messages ("code expired", "code not found", "no code detected"). Uses Meta Graph API to send text messages back through the same phoneNumberId. Provides instant, in-chat UX without requiring users to switch apps or check email.'
      },
      {
        name: 'ValidatedUtc timestamp tracking',
        summary: 'Records exact validation time for audit and analytics',
        detail: 'Updates OTPRequests.ValidatedUtc to NOW() when code is successfully validated. This timestamp enables analytics (validation rates, time-to-validate), prevents double-use, and provides audit trails for security investigations. NULL ValidatedUtc indicates unused codes; non-NULL indicates used codes with exact validation time.'
      }
    ],
    businessLogic: [
      { step: 1, title: 'CORS Handling', description: 'Handles OPTIONS preflight for webhook calls' },
      { step: 2, title: 'GET: Webhook Verification', description: 'Verifies hub.mode=subscribe and hub.verify_token, returns hub.challenge' },
      { step: 3, title: 'POST: Parse Webhook', description: 'Parses incoming WhatsApp message webhook from Meta' },
      { step: 4, title: 'Extract Message', description: 'Extracts sender (from), message text (body), and phoneNumberId from webhook' },
      { step: 5, title: 'Extract GUID', description: 'Uses regex to find UUID pattern in message text (OTP code)' },
      { step: 6, title: 'Normalize MSISDN', description: 'Converts sender phone number to E.164 format for lookup' },
      { step: 7, title: 'Lookup OTP Request', description: 'Queries OTPRequests by Code and Msisdn where ValidatedUtc IS NULL' },
      { step: 8, title: 'Validate Expiry', description: 'Checks if current time is before ExpiresUtc' },
      { step: 9, title: 'Mark as Validated', description: 'Updates OTPRequests.ValidatedUtc to NOW() if valid' },
      { step: 10, title: 'Send Reply Message', description: 'Sends WhatsApp text reply: success message or error (expired/not found)' },
      { step: 11, title: 'Return Response', description: 'Returns 200 with valid=true/false, reason, and normalized msisdn' }
    ],
    webhookSetup: {
      steps: [
        '1. Go to Meta Developer Portal → Your App → WhatsApp → Configuration',
        '2. Add callback URL: https://your-project.supabase.co/functions/v1/OTP-Validate-WhatsApp',
        '3. Set verify token in WHATSAPP_VERIFY_TOKEN env var (e.g., "nudgge-verify")',
        '4. Subscribe to webhook field: messages',
        '5. Test webhook - Meta sends GET to verify, then POST for incoming messages'
      ],
      verifyTokenNote: 'Set WHATSAPP_VERIFY_TOKEN and WHATSAPP_ACCESS_TOKEN in Supabase Edge Function secrets'
    },
    webhookPayloadExample: {
      entry: [{
        changes: [{
          value: {
            messages: [{
              from: '27821234567',
              text: {
                body: '3fa85f64-5717-4562-b3fc-2c963f66afa6'
              }
            }],
            metadata: {
              phone_number_id: '12345678'
            }
          }
        }]
      }]
    }
  }
]

export default function EdgeFunctionsTab() {
  const [selectedFunction, setSelectedFunction] = useState(null)
  const [hoveredFeature, setHoveredFeature] = useState(null)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-center space-x-3 mb-2">
          <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            Edge Functions
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          View and explore WhatsApp-related Supabase Edge Functions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Function List */}
        <div className="lg:col-span-1 space-y-3">
          {WHATSAPP_FUNCTIONS.map((func) => (
            <button
              key={func.name}
              onClick={() => setSelectedFunction(func)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedFunction?.name === func.name
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-green-300 dark:hover:border-green-700'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                      {func.name}
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                      {func.method}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {func.description}
                  </p>
                  <code className="text-xs text-green-600 dark:text-green-400 mt-2 block">
                    {func.endpoint}
                  </code>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Function Details */}
        <div className="lg:col-span-2">
          {selectedFunction ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              {/* Function Header */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-4 sm:p-6 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold">{selectedFunction.name}</h3>
                    <p className="text-green-100 mt-1 text-sm sm:text-base">{selectedFunction.description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedFunction(null)}
                    className="p-1 hover:bg-white/20 rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                    {selectedFunction.method}
                  </span>
                  <code className="px-3 py-1 bg-black/20 rounded-full text-sm font-mono">
                    {selectedFunction.endpoint}
                  </code>
                </div>
              </div>

              {/* Features Accordion */}
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Key Features
                </h4>
                <div className="space-y-2">
                  {selectedFunction.features.map((feature, idx) => {
                    const isExpanded = hoveredFeature === idx
                    const featureData = typeof feature === 'string' ? { name: feature } : feature
                    
                    return (
                      <div
                        key={idx}
                        onMouseEnter={() => setHoveredFeature(idx)}
                        onMouseLeave={() => setHoveredFeature(null)}
                        className={`border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-300 ${
                          isExpanded ? 'bg-green-50 dark:bg-green-900/10 border-green-300 dark:border-green-700' : 'bg-white dark:bg-gray-800'
                        }`}
                      >
                        {/* Header - Always Visible */}
                        <div className="flex items-start space-x-3 p-3">
                          <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <div className="flex-1">
                            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                              {featureData.name}
                            </h5>
                            {featureData.summary && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {featureData.summary}
                              </p>
                            )}
                          </div>
                          {featureData.detail && (
                            <svg 
                              className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${
                                isExpanded ? 'rotate-180' : ''
                              }`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          )}
                        </div>
                        
                        {/* Expanded Content */}
                        {featureData.detail && (
                          <div 
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                              isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                            }`}
                          >
                            <div className="px-3 pb-3 pt-0">
                              <div className="pl-8 border-l-2 border-green-300 dark:border-green-700">
                                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                  {featureData.detail}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Architectural Notes (if present) */}
              {selectedFunction.architecturalNotes && (
                <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/10">
                  <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Architectural Behavior</span>
                  </h4>
                  <ul className="space-y-2">
                    {selectedFunction.architecturalNotes.map((note, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-blue-800 dark:text-blue-200">{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Phases Overview (if present) */}
              {selectedFunction.phases && (
                <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center space-x-2">
                    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <span>Processing Phases</span>
                  </h4>
                  <div className="space-y-3">
                    {selectedFunction.phases.map((phase, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <h5 className="text-sm font-bold text-gray-900 dark:text-white mb-1">{phase.name}</h5>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{phase.purpose}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 italic">Why: {phase.why}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Business Logic Flow */}
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  {selectedFunction.phases ? 'Detailed Implementation Steps' : 'Business Logic Flow'}
                </h4>
                <div className="space-y-4">
                  {selectedFunction.businessLogic.map((logic, idx) => (
                    <div 
                      key={idx}
                      className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      {/* Phase Label */}
                      {logic.phase && (
                        <div className="mb-3">
                          <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-wide bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full">
                            {logic.phase}
                          </span>
                        </div>
                      )}
                      
                      {/* Main Content */}
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {logic.step}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0 space-y-2">
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {logic.title}
                          </h5>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                            {logic.description}
                          </p>
                          
                          {/* Layman Explanation */}
                          {logic.layman && (
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                              <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1">What this means:</p>
                              <p className="text-xs text-blue-800 dark:text-blue-200">{logic.layman}</p>
                            </div>
                          )}
                          
                          {/* Technical Details */}
                          {logic.technical && (
                            <details className="text-xs">
                              <summary className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 font-medium">Technical implementation</summary>
                              <div className="mt-2 p-3 bg-gray-800 dark:bg-gray-950 rounded border border-gray-700">
                                <code className="text-xs text-gray-300 font-mono">{logic.technical}</code>
                              </div>
                            </details>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Webhook Setup (only for Webhook-WhatsApp) */}
              {selectedFunction.webhookSetup && (
                <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-yellow-50 dark:bg-yellow-900/10">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center space-x-2">
                    <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span>Webhook Setup Instructions</span>
                  </h4>
                  <ol className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    {selectedFunction.webhookSetup.steps.map((step, idx) => (
                      <li key={idx} className="pl-4">{step}</li>
                    ))}
                  </ol>
                  <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded border border-yellow-200 dark:border-yellow-700">
                    <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200">
                      📌 {selectedFunction.webhookSetup.verifyTokenNote}
                    </p>
                  </div>
                </div>
              )}

              {/* API Example */}
              {selectedFunction.requestExample && (
                <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Request Example
                  </h4>
                  <pre className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto text-xs mb-4">
                    <code className="text-gray-800 dark:text-gray-200">{`curl -X ${selectedFunction.method.split(',')[0].trim()} \\
  'https://your-project.supabase.co${selectedFunction.endpoint}' \\
  -H 'X-Api-Key: ${selectedFunction.requestExample.headers['X-Api-Key']}' \\
  -H 'Content-Type: application/json' \\
  -d '${JSON.stringify(selectedFunction.requestExample.body, null, 2)}'`}</code>
                  </pre>

                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 mt-6">
                    Response Example (202 Accepted)
                  </h4>
                  <pre className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto text-xs">
                    <code className="text-gray-800 dark:text-gray-200">{JSON.stringify(selectedFunction.responseExample, null, 2)}</code>
                  </pre>
                </div>
              )}

              {/* Webhook Payload Example */}
              {selectedFunction.webhookPayloadExample && (
                <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Webhook Payload Example (from Meta)
                  </h4>
                  <pre className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto text-xs">
                    <code className="text-gray-800 dark:text-gray-200">{JSON.stringify(selectedFunction.webhookPayloadExample, null, 2)}</code>
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 sm:p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Select a Function
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose an Edge Function from the list to view its implementation and documentation
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
